import React, { useCallback, useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending review' },
  { id: 'approved', label: 'Approved' },
  { id: 'denied', label: 'Denied' },
  { id: 'all', label: 'All' },
];

const CATEGORY_LABEL = {
  veteran: 'Veteran',
  cancer: 'Cancer family',
  hardship: 'Family hardship',
  other: 'Other',
};

export default function AdminAssistanceRequests() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assistance/requests?status=${statusFilter}`);
      setRequests(data.requests || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (req) => {
    const dp = window.prompt(`Approve ${req.name}? Enter discount % (1–100):`, '50');
    if (dp === null) return;
    const discount_percent = Number(dp);
    if (!discount_percent || discount_percent < 1 || discount_percent > 100) {
      toast.error('Discount must be 1–100');
      return;
    }
    const days = window.prompt('Expires in how many days?', '60');
    if (days === null) return;
    try {
      const { data } = await api.post(`/admin/assistance/requests/${req.request_id}/approve`, {
        discount_percent,
        expires_in_days: Number(days) || 60,
      });
      toast.success(`Approved — code ${data.promo_code} emailed to ${req.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deny = async (req) => {
    const reason = window.prompt(`Deny ${req.name}? Reason (sent to them):`, 'Your request did not meet our current assistance criteria.');
    if (reason === null) return;
    try {
      await api.post(`/admin/assistance/requests/${req.request_id}/deny`, { reason });
      toast.success('Request denied — email sent.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
          <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-assistance-title">
            Assistance requests
          </h1>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
            Read every request carefully. Approving generates a one-time discount code that's emailed to the requester.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-8">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-4 py-2 rounded-full text-sm border transition ${
                statusFilter === t.id
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]'
                  : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.8)] hover:bg-[hsl(var(--primary)/0.04)]'
              }`}
              data-testid={`admin-assistance-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-assistance-empty">
            No requests match this filter.
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => (
              <div key={req.request_id} className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-6 py-5" data-testid={`admin-assistance-row-${req.request_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                      {CATEGORY_LABEL[req.category] || req.category}
                    </div>
                    <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-1">{req.name}</h3>
                    <div className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">{req.email}{req.phone ? ` · ${req.phone}` : ''}</div>
                    <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">Submitted {(req.submitted_at || '').slice(0,10)}</div>
                    {req.story && (
                      <button
                        onClick={() => setExpandedId(expandedId === req.request_id ? null : req.request_id)}
                        className="mt-3 text-xs text-[hsl(var(--accent))] hover:underline"
                        data-testid={`admin-assistance-expand-${req.request_id}`}
                      >
                        {expandedId === req.request_id ? 'Hide their note' : 'Read their note'}
                      </button>
                    )}
                    {expandedId === req.request_id && (
                      <p className="mt-3 text-sm text-[hsl(var(--primary)/0.8)] bg-[hsl(var(--primary)/0.04)] border border-[hsl(var(--primary)/0.1)] rounded p-3 whitespace-pre-wrap" data-testid={`admin-assistance-story-${req.request_id}`}>
                        {req.story}
                      </p>
                    )}
                    {req.promo_code && (
                      <div className="mt-3 text-xs text-emerald-700" data-testid={`admin-assistance-issued-code-${req.request_id}`}>
                        Code issued: <code className="font-mono">{req.promo_code}</code> · {req.discount_percent}% off · expires {(req.expires_at || '').slice(0,10)}
                      </div>
                    )}
                    {req.denial_reason && (
                      <div className="mt-3 text-xs text-red-700" data-testid={`admin-assistance-denial-${req.request_id}`}>
                        Denied: {req.denial_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${
                      req.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : req.status === 'denied' ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      {req.status}
                    </span>
                    {req.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => approve(req)} className="ml-btn-gold" data-testid={`admin-assistance-approve-${req.request_id}`}>Approve</button>
                        <button onClick={() => deny(req)} className="ml-btn-secondary text-red-700" data-testid={`admin-assistance-deny-${req.request_id}`}>Deny</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminLayout>
    </div>
  );
}
